// Competition definitions shared between web and functions. These are the static
// defaults; the functions layer applies env overrides (ids/seasons) on top before
// writing the League docs (see functions/src/lib/config.ts).
//
// API-Football league ids:
//   39  Premier League   140 La Liga   135 Serie A   119 Danish Superliga   1 World Cup
// `season` is the campaign's starting year (e.g. 2025 = the 2025/26 league season;
// 2026 = the FIFA World Cup 2026).
export const LEAGUE_KEYS = [
    'world-cup-2026',
    'premier-league',
    'la-liga',
    'serie-a',
    'superliga'
];
export const LEAGUE_DEFS = {
    'world-cup-2026': {
        leagueId: 'world-cup-2026',
        sport: 'FOOTBALL',
        // Display name is deliberately generic (NOT FIFA's protected "World Cup" mark,
        // and no year that ties it to the live FIFA tournament). This is only the
        // seed/fallback: the live display name is the Firestore `leagues/world-cup-2026.name`
        // field, which `ensureLeagues` seeds once then leaves alone — so the public name
        // (and the year-derived hero badge) can be flipped server-side without an app
        // resubmit. The Firestore doc id / leagueId stays `world-cup-2026` forever — it's
        // an internal key (never shown to users or App Review) and renaming it would
        // orphan every membership, entry, round and group keyed under it.
        name: 'Football Tournament',
        shortCode: 'WC',
        country: 'World',
        apiFootballLeagueId: 1,
        season: 2026,
        format: 'CUP',
        roundLabelPrefix: '',
        // A World Cup stage (e.g. "Group Stage - 1") spans several days. Slice it into
        // one tight pick-3 round per match-day, anchored to a US day so a night's
        // matches stay together (the 2026 hosts are in the Americas).
        roundChunking: 'BY_DAY',
        dayTimezone: 'America/New_York',
        statsEnabled: true
    },
    'premier-league': {
        leagueId: 'premier-league',
        sport: 'FOOTBALL',
        // Generic display fallback (App Store guideline 4.1(a): no third-party league
        // trademarks in the shipped bundle). The live name is the Firestore
        // `league.name`, which the UI prefers — flip it back to the real league name
        // server-side once approved, no resubmit needed.
        name: 'England League',
        shortCode: 'PL',
        country: 'England',
        apiFootballLeagueId: 39,
        season: 2025,
        format: 'LEAGUE',
        roundLabelPrefix: 'Gameweek',
        statsEnabled: true
    },
    'la-liga': {
        leagueId: 'la-liga',
        sport: 'FOOTBALL',
        name: 'Spain League',
        shortCode: 'LL',
        country: 'Spain',
        apiFootballLeagueId: 140,
        season: 2025,
        format: 'LEAGUE',
        roundLabelPrefix: 'Round',
        statsEnabled: true
    },
    'serie-a': {
        leagueId: 'serie-a',
        sport: 'FOOTBALL',
        name: 'Italy League',
        shortCode: 'SA',
        country: 'Italy',
        apiFootballLeagueId: 135,
        season: 2025,
        format: 'LEAGUE',
        roundLabelPrefix: 'Round',
        statsEnabled: true
    },
    superliga: {
        leagueId: 'superliga',
        sport: 'FOOTBALL',
        name: 'Denmark League',
        shortCode: 'SL',
        country: 'Denmark',
        apiFootballLeagueId: 119,
        season: 2025,
        format: 'LEAGUE',
        roundLabelPrefix: 'Round',
        statsEnabled: true
    }
};
/** Build a fresh League doc from a definition (no current round yet). */
export function leagueFromDef(def) {
    return {
        leagueId: def.leagueId,
        sport: def.sport,
        name: def.name,
        shortCode: def.shortCode,
        country: def.country,
        apiFootballLeagueId: def.apiFootballLeagueId,
        season: def.season,
        format: def.format,
        roundLabelPrefix: def.roundLabelPrefix,
        roundChunking: def.roundChunking ?? 'BY_ROUND',
        dayTimezone: def.dayTimezone ?? null,
        currentRoundId: null
    };
}
/** Deterministic Firestore id for a cycling race league, e.g. `cycling-tour-de-france-2026`. */
export function cyclingLeagueId(slug, year) {
    return `cycling-${slug}-${year}`;
}
/**
 * Derive a short crest code from a league name when no curated `shortCode` exists
 * (e.g. dynamically-minted cycling races): the leading letter of up to the first
 * three words — "Tour de Suisse" → "TDS", "Paris-Roubaix" → "PR" — or the first
 * three letters of a single-word name. Falls back to "?" only for an empty name.
 */
export function leagueShortCode(name) {
    const words = (name ?? '').split(/[\s'’.\-–—]+/u).filter(Boolean);
    if (words.length === 0)
        return '?';
    if (words.length === 1)
        return words[0].slice(0, 3).toUpperCase();
    return words
        .slice(0, 3)
        .map((w) => w[0])
        .join('')
        .toUpperCase();
}
/** Build a fresh cycling League doc from a curated race seed (no current round yet). */
export function cyclingLeagueFromSeed(seed) {
    return {
        leagueId: cyclingLeagueId(seed.slug, seed.year),
        sport: 'CYCLING',
        name: seed.name,
        shortCode: seed.shortCode ?? leagueShortCode(seed.name),
        country: seed.country,
        apiFootballLeagueId: 0, // n/a — cycling has no API-Football id
        season: seed.year,
        format: 'LEAGUE',
        roundLabelPrefix: 'Stage',
        roundChunking: 'BY_ROUND',
        dayTimezone: null,
        cycling: {
            slug: seed.slug,
            year: seed.year,
            group: seed.group,
            startsAt: seed.startsAt ?? null,
            endsAt: seed.endsAt ?? null
        },
        currentRoundId: null
    };
}
//# sourceMappingURL=leagues.js.map